import React, { useCallback, useState, useEffect } from 'react';
import c from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import { BiData } from '@wix/da-bi/pkg/events';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import ModalFooterMobile from '@wix/da-ds/pkg/modals/dsPresets/ModalFooterMobile';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import FragmentsValue from '../../FragmentsValue';
import { getRecipientSectionName } from '../../utils';
import SelectedBadgeAndDescription from '../SelectedBadgeAndDescription';
import BadgeCard from '../BadgeCard';
import NeedMoreFragments from '../NeedMoreFragments';
import ModalCTAButton from '../ModalCTAButton';
import s from './MobileAwardBadgeModal.scss';
const WHAT_ARE_BADGES_KB_URL = 'https://www.deviantartsupport.com/en/article/what-are-badges-and-fragments';
const BADGE_COUNT_WHERE_SPACING_CHANGES = 4;
const MobileAwardBadgeModal = ({ hasCore, coreLevel, recipient, typeId, itemId, balance, badges, isOpen, isAwarding, errorMsg, awardBadge, fetchAvailableBadges, requestClose })=>{
    const { t } = useTranslation();
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showError, setShowError] = useState(false);
    useEffect(()=>{
        fetchAvailableBadges(recipient.user.userId, typeId, itemId);
    }, [
        recipient,
        fetchAvailableBadges,
        typeId,
        itemId
    ]);
    useEffect(()=>{
        if (badges.length > 0) {
            setSelectedIndex(0);
        }
    }, [
        badges
    ]);
    useEffect(()=>{
        setShowError(errorMsg ? true : false);
    }, [
        errorMsg
    ]);
    const changeSelectedBadge = (idx)=>{
        setSelectedIndex(idx);
        setShowError(false);
    };
    const selectedBadge = selectedIndex !== null ? badges[selectedIndex] : undefined;
    const awardSelectedBadge = useCallback(()=>selectedBadge && awardBadge(selectedBadge.badge.name, recipient), [
        selectedBadge,
        awardBadge,
        recipient
    ]);
    const cantAffordSelected = selectedBadge && selectedBadge.price > balance;
    const manyBadges = badges.length >= BADGE_COUNT_WHERE_SPACING_CHANGES;
    const renderModalContent = ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, selectedBadge && /*#__PURE__*/ React.createElement(SelectedBadgeAndDescription, {
            className: c(badges.length > 3 && s['has-special-edition']),
            selectedBadge: selectedBadge,
            recipient: recipient,
            showLock: cantAffordSelected
        }), cantAffordSelected && /*#__PURE__*/ React.createElement(NeedMoreFragments, {
            hasCore: hasCore,
            coreLevel: coreLevel,
            badgeTitle: selectedBadge.badge.title
        }), showError && /*#__PURE__*/ React.createElement("div", {
            className: s['error']
        }, errorMsg), /*#__PURE__*/ React.createElement("div", {
            className: c(s['badges'], manyBadges && s['tightened-spacing']),
            role: "radiogroup"
        }, badges.map((badge, idx)=>/*#__PURE__*/ React.createElement(BadgeCard, {
                key: badge.badge.name,
                className: s['badge'],
                badge: badge,
                selected: badge === selectedBadge,
                showLock: badge.price > balance,
                biData: BiData({
                    evid: 322,
                    badge_typeid: badge.badge.typeId,
                    badge_type: badge.badge.name,
                    sectiontype: 'award_badge',
                    sectionname: getRecipientSectionName(recipient.typeId),
                    typeid: recipient.typeId,
                    itemid: recipient.itemId
                }),
                onClick: ()=>changeSelectedBadge(idx)
            }))));
    return /*#__PURE__*/ React.createElement(DSModal, {
        className: s['root'],
        isOpen: !!isOpen,
        onRequestClose: requestClose,
        showHeaderBorder: true,
        showFooterBorder: true,
        isFullscreen: true,
        useFixedMobileHeight: true,
        contentLabel: t('coreBadge.awardBadgeModal.heading'),
        heading: t('coreBadge.awardBadgeModal.headingWithUsername', {
            username: recipient.user.username
        }),
        subheading: /*#__PURE__*/ React.createElement("div", {
            className: s['balance']
        }, /*#__PURE__*/ React.createElement(Trans, {
            i18nKey: "coreBadge.awardBadgeModal.balance",
            count: balance
        }, "You have ", /*#__PURE__*/ React.createElement(FragmentsValue, {
            amount: balance,
            className: s['balance-value']
        }))),
        footerClassName: s['footer-section'],
        footer: /*#__PURE__*/ React.createElement(ModalFooterMobile, {
            className: s['footer'],
            primaryButton: /*#__PURE__*/ React.createElement(ModalCTAButton, {
                className: s['give-button'],
                mode: cantAffordSelected ? !hasCore ? 'link-to-core' : 'close-modal' : 'award-badge',
                selectedBadge: selectedBadge,
                recipient: recipient,
                disabled: isAwarding,
                awardSelectedBadge: awardSelectedBadge,
                requestClose: requestClose
            }),
            bottomExtras: /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
                className: s['what-are-badge-awards'],
                target: "_blank",
                href: WHAT_ARE_BADGES_KB_URL,
                biData: BiData({
                    evid: 324,
                    sectionname: 'give_badges',
                    itemid: recipient.itemId,
                    typeid: recipient.typeId
                })
            }, t('coreBadge.awardBadgeModal.linkToKB'))
        })
    }, badges.length === 0 ? /*#__PURE__*/ React.createElement("div", {
        className: s['content-wrapper']
    }, /*#__PURE__*/ React.createElement(LoadingIndicator, null)) : renderModalContent());
};
MobileAwardBadgeModal.displayName = 'MobileAwardBadgeModal';
export default MobileAwardBadgeModal;

//# sourceMappingURL=MobileAwardBadgeModal.js.map